import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Folio, Engraving, Glossarium, Marginalia, StaffExample } from "@/components/sn/SNShell";
import { Notation } from "@/components/Notation";

export default function StaffNotation4() {
  const { lesson, course, accent } = findLesson("staff-notation", "4");
  return (
    <LessonShell
      course={course}
      courseSlug="staff-notation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/staff-notation/SN4-Note-Values-Animated.mp4"
        caption="Beats, note values & rests — the halving tree"
        hint="≈ 24 sec · watch first"
      />
      <Folio number="I" title="Hit, miss, or hold — the only three things you can do">
        <p>
          The x-axis of music is time. We've covered the y-axis — pitch — in detail. Now Jason
          moves to the micro level of the x-axis: note values. And he starts not with symbols
          but with a question about what any musician can actually <em>do</em> at a given moment.
        </p>
        <p>
          The answer: exactly three things. You can <strong>hit</strong> a note. You can{" "}
          <strong>miss</strong> — do nothing. Or you can <strong>hold</strong> — sustain something
          you already hit. That's the whole universe of musical events. Hit, miss, or hold.
        </p>
        <Glossarium>
          <p>
            <strong>Hit</strong> — you strike a note. Also called an <em>attack</em>. Could be
            one note, two notes, a chord — but the event is an attack. A sound begins.
          </p>
          <p>
            <strong>Miss</strong> — you play nothing. Called a <em>rest</em>. Not a mistake —
            silence is counted as carefully as sound.
          </p>
          <p>
            <strong>Hold</strong> — you sustain a note that has already begun. Also called a{" "}
            <em>sustain</em>. The note was attacked earlier; now you are holding it down, and
            it's still vibrating. How long is it held? That is the question.
          </p>
        </Glossarium>
        <p>
          Everything in this lesson — every note shape and every rest shape — is expressing one
          of those three events, for a specific amount of time. That time unit is the{" "}
          <strong>beat</strong>.
        </p>
      </Folio>

      <Folio number="II" title="The beat — a time container for a musical event">
        <p>
          Jason's definition is precise: <em>"A beat is defined as a time container for a musical event."</em>{" "}
          Not a sound, not a tap — a container. You fill it with a hit, a miss, or a hold.
          And the beat is generally associated with the pulse — how your body naturally moves
          to music: 1, 2, 3, 4, or whatever the song demands.
        </p>
        <p>
          Now the question is: how do we write down how long each event lasts? How does a symbol
          tell you "this hit lasts four beats" or "this rest lasts half a beat"? That's what
          note values are: a visual grammar for the duration of musical events.
        </p>
        <p>
          Jason goes through the values in order from longest to shortest — and crucially, he
          includes both their British names (which describe what the note looks like) and their
          American names (which describe their fraction of a bar).
        </p>
      </Folio>

      <Folio number="III" title="The values — from four beats down to a quarter beat">
        <p>
          Six note values carry almost all the music you will ever play or read. Jason lists
          them in this order, from widest to narrowest:
        </p>
        <Glossarium>
          <p>
            <strong>Semibreve</strong> (whole note) — <strong>4 beats</strong>. A hollow oval,
            no stem. Like an egg. The rest is a filled rectangle hanging below the fourth line.
            "You could argue that semibreve is more European or British," Jason says; whole
            note is the American name.
          </p>
          <p>
            <strong>Dotted minim</strong> (dotted half note) — <strong>3 beats</strong>. Not a
            new symbol so much as a modifier: a minim with a dot after it. The dot adds half
            the note's own value, so 2 + 1 = 3. A dot works on any note value, and on rests too.
          </p>
          <p>
            <strong>Minim</strong> (half note) — <strong>2 beats</strong>. A hollow oval with
            a stem. The rest sits on top of the middle line — a block resting on the third line.
          </p>
          <p>
            <strong>Crotchet</strong> (quarter note) — <strong>1 beat</strong>. A filled oval
            with a stem. The rest is the squiggle — a distinctive shape Jason describes as
            "interesting."
          </p>
          <p>
            <strong>Quaver</strong> (eighth note) — <strong>½ beat</strong>. A filled oval, stem
            with one flag. The rest is a small flag-shape.
          </p>
          <p>
            <strong>Semiquaver</strong> (sixteenth note) — <strong>¼ beat</strong>. A filled oval,
            stem with two flags. The rest stacks two flags.
          </p>
        </Glossarium>
        <Marginalia>
          There are shorter values — thirty-second notes, sixty-fourth — but for virtually all
          music you will play or read, these six values are sufficient. "There are more, but
          for the most part, this is what you're going to use in a song," Jason says.
        </Marginalia>
      </Folio>

      <StaffExample caption="The common note values on middle C — whole (4 beats), half (2), quarter (1), eighth (½).">
        <Notation
          measures={[
            { chords: [{ keys: ["C4"], duration: "w" }] },
            {
              chords: [
                { keys: ["C4"], duration: "h" },
                { keys: ["C4"], duration: "h" },
              ],
            },
            {
              chords: [
                { keys: ["C4"], duration: "q" },
                { keys: ["C4"], duration: "q" },
                { keys: ["C4"], duration: "q" },
                { keys: ["C4"], duration: "q" },
              ],
            },
            {
              chords: [
                { keys: ["C4"], duration: "8" },
                { keys: ["C4"], duration: "8" },
                { keys: ["C4"], duration: "8" },
                { keys: ["C4"], duration: "8" },
                { keys: ["C4"], duration: "8" },
                { keys: ["C4"], duration: "8" },
                { keys: ["C4"], duration: "8" },
                { keys: ["C4"], duration: "8" },
              ],
            },
          ]}
          timeSig="4/4"
          clef="treble"
          width={760}
          height={150}
        />
      </StaffExample>

      <Folio number="IV" title="Beaming — the rule you must not break">
        <p>
          When quavers or semiquavers appear in sequence, they are never drawn with individual
          flags floating separately. They are <strong>beamed</strong>: connected by a solid bar
          across the tops of their stems. Jason is emphatic: in instrumental music, drawing
          unbeamed quavers in the middle of a bar is <em>"absolutely wrong."</em> (Vocal
          writing is the one exception — there each note may carry its own syllable, so the
          flags stay separate.)
        </p>
        <p>
          The beam turns a pair of quavers into one readable unit — one beat divided into two.
          Four semiquavers under a double beam are one readable beat divided into four. The eye
          grasps the rhythm at a glance instead of counting individual flags. This is not a
          stylistic preference — beaming is the standard in all conventional music notation.
        </p>
        <p>
          Beaming applies to quavers, semiquavers, and tuplets (which Jason will cover later).
          The rule: if two or more flagged notes fall within a beat group, beam them.
        </p>
      </Folio>

      <Folio number="V" title="Counting — one and, one e and a">
        <p>
          To practise rhythm, Jason teaches counting aloud. The beat is always 1, 2, 3, 4.
          Dividing beats adds syllables:
        </p>
        <Glossarium>
          <p>
            <strong>Quarter notes:</strong> "1, 2, 3, 4" — one count per beat.
          </p>
          <p>
            <strong>Eighth notes (quavers):</strong> "1 and, 2 and, 3 and, 4 and" — the beat
            splits in two. The "and" lands exactly halfway through the beat. This is where
            musicians enter the "world of dividing the beat by two," Jason says.
          </p>
          <p>
            <strong>Sixteenth notes (semiquavers):</strong> "1 e and a, 2 e and a, 3 e and a,
            4 e and a" — each beat divides into four equally spaced syllables. Say them at an
            even speed; the precision is the point.
          </p>
        </Glossarium>
        <p>
          You can also add rests into any of these patterns. A beat with a quaver rest followed
          by a quaver note? Count "1 and" — silence on 1, sound on "and." Practice this
          wherever you are: in a cab, walking, waiting. The beat is always passing; the counting
          just makes it visible.
        </p>
      </Folio>

      <Engraving
        src="/notes/staff-notation/finals/04-beats-note-values-and-rests-page1.png"
        caption="Jason's note value chart — semibreve to semiquaver, with their rests. Hit, miss, or hold — for 4, 3, 2, 1, ½, or ¼ of a beat."
      />
    </LessonShell>
  );
}
